// Connector Incidents (admin-portal) — the platform connector-health console: degraded
// connections and ones with recent failed sync runs, across all tenants. Read-only; the
// worker auto-degrades a connection to `error` on terminal failure (and recovers it on a
// successful run), so this is a live triage view rather than a manual incident log.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Row, Space, Statistic, Table, Tag, Tooltip, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import * as api from '../../services/connectorIncidentsService';

const { Title, Text, Paragraph } = Typography;
const errMsg = (e: any, fb: string) =>
  e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fb;
const fmt = (iso?: string) => (iso ? fmtDateTime(iso) : '—');

export const ConnectorIncidentsPage: React.FC = () => {
  const [rows, setRows] = useState<api.ConnectorIncident[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.getConnectorIncidents());
    } catch (e) {
      message.error(errMsg(e, 'Could not load connector incidents'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    degraded: rows.filter((r) => r.status === 'error').length,
    failing: rows.filter((r) => r.failed_runs_7d > 0).length,
    tenants: new Set(rows.map((r) => r.tenant_id)).size,
  }), [rows]);

  const columns = [
    { title: 'Connection', dataIndex: 'name', render: (v: string, r: api.ConnectorIncident) => <Space direction="vertical" size={0}><Text strong>{v}</Text><Text type="secondary" style={{ fontSize: 11 }} code>{r.connector_key}</Text></Space> },
    { title: 'Tenant', dataIndex: 'tenant_id', render: (v: string) => <Text style={{ fontSize: 11 }} copyable={{ text: v }}>{v.slice(0, 8)}…</Text> },
    {
      title: 'Status', dataIndex: 'status',
      render: (v: string) => v === 'error'
        ? <Tag color="red">degraded</Tag>
        : <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag>,
    },
    {
      title: 'Failed (7d)', dataIndex: 'failed_runs_7d',
      sorter: (a: api.ConnectorIncident, b: api.ConnectorIncident) => a.failed_runs_7d - b.failed_runs_7d,
      render: (v: number) => (v > 0 ? <Tag color="orange">{v}</Tag> : '0'),
    },
    { title: 'Last sync', dataIndex: 'last_sync_at', render: (v: string) => <Text style={{ fontSize: 12 }}>{fmt(v)}</Text> },
    {
      title: 'Last error', dataIndex: 'last_error',
      render: (v: string) => v
        ? <Tooltip title={v}><Text type="danger" style={{ fontSize: 12 }} ellipsis={{ tooltip: false }}>{v.length > 80 ? v.slice(0, 80) + '…' : v}</Text></Tooltip>
        : <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>Connector Incidents</Title>
          <Paragraph type="secondary" style={{ maxWidth: 720 }}>
            Degraded integration connections and ones with failed syncs in the last 7 days, across all tenants.
            A connection auto-degrades to <Tag color="red" style={{ margin: 0 }}>degraded</Tag> after a terminal
            sync failure and recovers on the next success — this is a live triage view.
          </Paragraph>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
      </Space>

      <Row gutter={16} style={{ margin: '8px 0 16px' }}>
        <Col xs={8}><Card size="small" loading={loading}><Statistic title="Degraded" value={stats.degraded} valueStyle={stats.degraded ? { color: 'var(--color-danger)' } : undefined} /></Card></Col>
        <Col xs={8}><Card size="small" loading={loading}><Statistic title="Failing (7d)" value={stats.failing} valueStyle={stats.failing ? { color: 'var(--color-warning)' } : undefined} /></Card></Col>
        <Col xs={8}><Card size="small" loading={loading}><Statistic title="Tenants affected" value={stats.tenants} /></Card></Col>
      </Row>

      <Card size="small">
        {!loading && rows.length === 0 ? (
          <Alert type="success" showIcon message="No connector incidents — all connections healthy." />
        ) : (
          <Table rowKey="connection_id" size="small" loading={loading} columns={columns as any} dataSource={rows} pagination={{ pageSize: 20 }} />
        )}
      </Card>
    </div>
  );
};

export default ConnectorIncidentsPage;
